<template>
  <div class="view-shortlist-container column">
    <LeaseInclusionModal v-model="showLeaseInclusion" />

    <div class="view-shortlist-header">
      <p class="text-center text-weight-bold" style="margin-bottom: 7px">
        The estimated payments shown are calculated based on travelling 15,000km annually over the lease term selected below.
      </p>
      <p class="text-center text-weight-bold">
        You can change the results to weekly or monthly payments and compare a 3, 4 or 5 year lease term.
      </p>
    </div>
    <div class="view-shortlist-body row">
      <div class="view-shortlist-action-group row justify-between">
        <CustomExpansionItem
          class="filter-action-button q-mb-sm"
          :icon="sliderIcon"
          :headerStyle="{ color: 'var(--q-color-primary)' }"
          innerClass=""
          label="Customise"
          v-model="showFilterTransition"
        />
        <div class="filter-action-desktop row">
          <LeaseTermOptions class="lease-term-options q-mb-sm q-mr-sm" v-model="leaseTerm" :options="leaseTermOptions" />
          <div class="lease-term-tablet q-mb-sm q-mr-sm">
            <p class="text-primary text-weight-bolder" style="font-size: 16px; margin: 0 0 5px">Lease term</p>
            <QSelect
              v-model="leaseTerm"
              :options="leaseTermOptions"
              option-value="value"
              option-label="label"
              outlined
              class="calculator-select q-mr-md"
              style="width: 140px"
            />
          </div>
          <LeaseDistance class="q-mb-sm q-mr-sm" v-model="leaseDistance" />
          <RepaymentOptions class="q-mb-sm q-mr-sm" v-model="repaymentOption" />
        </div>
        <!-- <CalculatorButton
          class="self-end q-mb-sm"
          :color="undefined"
          rounded
          hasChevronIcon
          label="Request a call"
          outlined
          removeOpacity
          @click="handleRequestCallbackDialog"
        /> -->
        <CalculatorButton
          class="self-end q-mb-sm lease-inclusion"
          :color="showLeaseInclusion ? 'secondary' : undefined"
          rounded
          hasChevronIcon
          label="Lease inclusions"
          outlined
          removeOpacity
          @click="handleLeaseInclusionDialog"
        />

        <div class="lt-md q-mb-sm lease-distance-mb">
          <LeaseDistance v-model="leaseDistance" />
        </div>
        <QSlideTransition>
          <div v-show="showFilterTransition" class="filter-action-transition full-width">
            <QCard v-if="showFilterContainer" class="filter-action-card column q-my-sm q-pa-sm full-width" flat bordered>
              <RepaymentOptions class="q-mb-sm repayment_mobile" v-model="repaymentOption" />
              <LeaseTermOptions class="q-mb-sm q-mr-sm lease-term" v-model="leaseTerm" :options="leaseTermOptions" />
            </QCard>
          </div>
        </QSlideTransition>
      </div>
      <div class="mobile-view-shortlist-header">
        <p class="text-center text-weight-bold" style="margin-bottom: 7px">
          The estimated payments shown are calculated based on travelling 15,000km annually over the lease term selected below.
        </p>
        <p class="text-center text-weight-bold">
          You can change the results to weekly or monthly payments and compare a 3, 4 or 5 year lease term.
        </p>
      </div>
      <div
        class="view-shortlist-results col-12 row"
        :style="formattedShortlist.length >= 3 && 'justify-content: space-between'"
        v-if="!isFetchRatebookLoading"
      >
        <VehicleShortlistCard
          v-for="(shortlist, index) in formattedShortlist"
          :key="index"
          class="view-shortlist-result-card"
          :imagePath="shortlist.jatoImagepath"
          :imagePathBase="shortlist.jatoImagepathBase"
          :imagePathTrim="shortlist.jatoImagepathTrim"
          :fuelType="`${shortlist.catalogYear} | ${shortlist.vehiclePrimaryfueltype} | ${shortlist.vehicleTransmissiongroup}`"
          :vehicleDescription="formatDescription(shortlist)"
          :vehicleName="`${formatWordsAsSentences(shortlist.vehicleMake)} ${formatWordsAsSentences(shortlist.vehicleModel)} ${
            shortlist.vehicleTrim || ''
          } |  ${shortlist.vehicleDrivetrain}`"
          :vehicleMake="shortlist.vehicleMake"
          :weeklyPrice="shortlist.weekLeaseprice"
          :monthlyPrice="shortlist.monthLeaseprice"
          :rrp="shortlist.consumerPrice"
          :repaymentOption="repaymentOption"
          :showPaymentBreakdown="false"
          @onViewVehicle="onViewVehicle(shortlist)"
          @onRemoveShortlist="handleRemoveShortlist(shortlist)"
        />
      </div>
      <!-- <RequestCallbackModal v-model="showRequestCallback" /> -->
    </div>

    <div class="view-shortlist-footer q-mt-xl row justify-between">
      <CalculatorButton
        rounded
        hasChevronIcon
        label="Edit shortlist"
        outlined
        iconDirection="left"
        removeOpacity
        @click="handleEditShortlist"
      />

      <CalculatorButton color="primary" rounded hasChevronIcon label="Enquire now" outlined removeOpacity @click="handleEnquireNow" />
    </div>
  </div>
</template>

<script>
import { QSelect, QCard, QSlideTransition } from 'quasar';
import LeaseInclusionModal from '../../components/Calculator/LeaseInclusion/LeaseInclusionModal';
import RequestCallbackModal from '../../components/Calculator/RequestCallback/Modal';
import { fetchRatebookMixin, shortListOperationsMixin } from '../../mixins/calculator';
import { formatWordsAsSentences } from '../../utils/calculator/wordFormatter';
import RepaymentOptions from '../../components/Calculator/RepaymentOptions';
import VehicleShortlistCard from '../../components/Calculator/ViewShortlist/VehicleShortlistCard';
import LeaseTermOptions from '../../components/Calculator/LeaseTermOptions';
import LeaseDistance from '../../components/Calculator/LeaseDistance';
import CalculatorButton from '../../components/Calculator/CalculatorButton';
import { evaCloseCircleOutline, evaOptions2 } from '@quasar/extras/eva-icons';
import { CalculatorDialog } from '../../components/Calculator/CalculatorDialog';
import CustomExpansionItem from '../../components/Calculator/CustomExpansionItem';
import { debounce } from 'lodash';
import { mapActions } from 'vuex';
import {
  DEFAULT_LEASE_TERM,
  GTM_EVENT_NAME_SME_LEASE_CALCULATOR_CAR,
  GTM_EVENT_STAGE_VIEW_ENQUIRY,
  GTM_EVENT_VALUES_LEASE_CALCULATOR_CAR,
  LEASE_TERM_OPTIONS,
  REPAYMENT_WEEKLY
} from '../../utils/constants';
import {
  buildQueryWithBudgetType,
  buildQueryWithLeaseTerm,
  readLeaseTermFromQuery,
  readRepaymentFromQuery
} from '../../utils/calculator/calculatorPageQueryState';

export default {
  name: 'ViewShortlistPage',
  data() {
    return {
      vehicleLeaseTerms: [],
      leaseDistance: 15000,
      showLeaseInclusion: false,
      showRequestCallback: false,
      repaymentOption: REPAYMENT_WEEKLY,
      leaseTerm: {},
      showFilterContainer: true,
      showFilterTransition: false,
      leaseTermOptions: LEASE_TERM_OPTIONS
    };
  },
  mixins: [fetchRatebookMixin, shortListOperationsMixin],
  methods: {
    formatWordsAsSentences,
    ...mapActions({
      setAppLoading: 'app/setAppLoading'
    }),
    initializeLeaseTerm() {
      this.leaseTerm = readLeaseTermFromQuery(this.$route.query, this.leaseTermOptions, DEFAULT_LEASE_TERM);
    },
    initializeRepayment() {
      this.repaymentOption = readRepaymentFromQuery(this.$route.query);
    },
    syncLeaseTermQuery(value) {
      const nextQuery = buildQueryWithLeaseTerm(this.$route.query, value, this.leaseTermOptions, DEFAULT_LEASE_TERM);

      if (!nextQuery) return;

      this.$router.replace({
        name: this.$route.name,
        params: this.$route.params,
        query: nextQuery
      });
    },
    syncRepaymentQuery(value) {
      const nextQuery = buildQueryWithBudgetType(this.$route.query, value);

      if (!nextQuery) return;

      this.$router.replace({
        name: this.$route.name,
        params: this.$route.params,
        query: nextQuery
      });
    },
    handleRequestCallbackDialog() {
      this.showRequestCallback = true;
    },
    handleLeaseInclusionDialog() {
      this.showLeaseInclusion = true;
    },
    handleEditShortlist() {
      this.$router.push({ name: 'calculatorResults', query: { ...this.queryParams } });
    },
    onViewVehicle(vehicle) {
      this.$router.push({
        name: 'vehicleProduct',
        params: {
          catalogVehicleId: vehicle.catalogVehicleId,
          vehicleMake: vehicle.vehicleMake,
          vehicleModel: vehicle.vehicleModel,
          vehicleTrim: vehicle.vehicleTrim ? vehicle.vehicleTrim.toUpperCase() : undefined,
          vehicleDrivetrain: vehicle.vehicleDrivetrain ? vehicle.vehicleDrivetrain.toUpperCase() : undefined
        },
        query: {
          ...this.$route.query
        }
      });
    },

    handleEnquireNow() {
      setTimeout(() => {
        this.$gtm.trackEvent({
          event: GTM_EVENT_NAME_SME_LEASE_CALCULATOR_CAR,
          stage: GTM_EVENT_STAGE_VIEW_ENQUIRY,
          data: {
            ...GTM_EVENT_VALUES_LEASE_CALCULATOR_CAR
          }
        });
      });

      // eslint-disable-next-line no-console
      console.log('CURRENT SHORTLIST: ', this.shortlist);
      this.$router.push({
        name: 'calculatorRegistration',
        query: { ...(this.$route.query || {}) }
      });
    },
    handleEmptyShortlistRedirection() {
      if (!this.shortlist.length) {
        this.$euiNotifications.emit({
          notification: CalculatorDialog,
          headerIcon: evaCloseCircleOutline,
          header: 'No vehicles are shortlisted.',
          body: 'Please shortlist at least one vehicle to proceed.',
          closeFooterLabel: 'Return to search results'
        });

        this.$router.push({ name: 'calculatorResults', query: { ...this.queryParams } });
      }
    },
    formatDescription(vehicle) {
      const removeDetails = ['vehicleMake', 'vehicleModel', 'vehicleTrim'];
      let { vehicleDescription } = vehicle;

      if (!vehicleDescription) {
        return '';
      }

      removeDetails.forEach((detail) => {
        if (vehicle[detail]) {
          vehicleDescription = vehicleDescription.replace(vehicle[detail], '');
          vehicleDescription = vehicleDescription.replace(vehicle[detail].toUpperCase(), '');
          vehicleDescription = vehicleDescription.replace(vehicle[detail].replace(' ', '').toUpperCase(), '');
          vehicleDescription = vehicleDescription.replace(vehicle[detail].toLowerCase(), '');
        }
      });

      return vehicleDescription;
    },
    async getVehicleLeaseTerm() {
      const {
        results: { rows: ratebook, totalCount }
      } = await this.fetchRatebook({
        doNotIncludePair: true,
        vehicleMake: undefined,
        catalogVehicleId: this.shortlist?.map(({ catalogVehicleId }) => catalogVehicleId) || [],
        pageSize: 100,
        minDuration: 36,
        maxDuration: 60
      });

      const formattedLeaseTerms = this.shortlist.map((value) => {
        let leaseTerms = [];

        if (totalCount) {
          leaseTerms = ratebook
            .filter(({ catalogVehicleId }) => catalogVehicleId === value.catalogVehicleId)
            .map(({ catalogPrice, consumerPrice, monthLeaseprice, weekLeaseprice, duration, durationUnit }) => ({
              catalogPrice,
              consumerPrice,
              monthLeaseprice,
              weekLeaseprice,
              duration,
              durationUnit
            }));
        }

        return {
          ...value,
          vehicleTrim: value?.vehicleTrim || '',
          leaseTerms
        };
      });

      this.vehicleLeaseTerms = formattedLeaseTerms;
    },
    handleRemoveShortlist(value) {
      this.shortlist = this.shortlist.filter((vehicle) => vehicle.catalogVehicleId !== value.catalogVehicleId);
      this.vehicleLeaseTerms = this.vehicleLeaseTerms.filter((vehicle) => vehicle.catalogVehicleId !== value.catalogVehicleId);

      this.updateShortList();
      this.handleEmptyShortlistRedirection();
    }
  },
  computed: {
    formattedShortlist() {
      return this.vehicleLeaseTerms.map((value) => {
        const leaseTerm = value.leaseTerms.find((leaseTerm) => this.leaseTerm.value === leaseTerm.duration);

        return {
          ...value,
          ...leaseTerm
        };
      });
    },
    queryParams() {
      return this.$route.query;
    }
  },
  watch: {
    'showFilterTransition'(value) {
      debounce(() => {
        this.showFilterContainer = value;
      }, 100);
    },
    'leaseTerm': {
      handler(value) {
        this.syncLeaseTermQuery(value);
      },
      deep: true
    },
    'repaymentOption'(value) {
      this.syncRepaymentQuery(value);
    },
    '$route.query.budgetType': {
      handler() {
        this.initializeRepayment();
      }
    },
    '$route.query.leaseTerm': {
      handler() {
        this.initializeLeaseTerm();
      }
    },
    'isFetchRatebookLoading'() {
      this.setAppLoading(this.isFetchRatebookLoading);
    }
  },
  created() {
    this.handleEmptyShortlistRedirection();
    // icons
    this.sliderIcon = evaOptions2;
    // business logic
    this.updateShortList();
    this.initializeLeaseTerm();
    this.initializeRepayment();
    this.getVehicleLeaseTerm();
  },
  mounted() {
    this.setAppLoading(false);
  },
  components: {
    QSelect,
    QCard,
    QSlideTransition,
    LeaseInclusionModal,
    VehicleShortlistCard,
    RepaymentOptions,
    LeaseTermOptions,
    CalculatorButton,
    LeaseDistance,
    CustomExpansionItem
    // RequestCallbackModal
  }
};
</script>

<style lang="sass" scoped>
.view-shortlist-container
  max-width: 1094px
  width: 100%
  .calculator-button.q-btn
    height: auto!important
  .view-shortlist-action-group
    width: 100%
  .view-shortlist-results
    width: 100%
    justify-content: space-around
    .view-shortlist-result-card.q-card
      max-width: 335px
.lease-term-tablet
  width: 140px
  display: none
.filter-action-button
  display: none
  width: calc(50% - 10px)
  margin-bottom: 10px
  align-self: center;
  .q-item
    min-height: 40px

.filter-action-transition
  display: none
.filter-action-card
  display: none
@media (max-width: 1080px)
  .view-shortlist-container .view-shortlist-results
    align-items: flex-start;
    .view-shortlist-result-card.q-card
      max-width: 100%!important
      width: 100%
      margin-bottom: 10px

@media (max-width: 961px)
  .lease-term-options
    display:none!important
  .lease-term-tablet
    display:block!important
  .lease-distance-container
    width: 155px
@media (max-width: 735px)
  .filter-action-desktop
    display: none

  .filter-action-button
    display: block
  .q-btn.calculator-button
    width: calc(50% - 10px)
    align-self: center;
  .filter-action-transition
    display: block
  .filter-action-card
    display: block
  .two-col-grid
    display:grid
    grid-template-columns: auto auto
    grid-gap: 10px

.mobile-view-shortlist-header
  display: none

@media (max-width:735px)
  .view-shortlist-header
    display: none
  .view-shortlist-action-group
    position: sticky
    z-index: 1000
    top: 158px
    left: 0
    background: white
    padding: 10px 0 0 0
  .mobile-view-shortlist-header
    display: block
  .view-shortlist-footer
    margin-top: 0
    position: fixed
    bottom: 0
    width: 100%
    background: #fff
    padding: 10px
    left: 0
    z-index: 99
    height: 80px
  .lease-distance-mb
    width: 50%
  .q-btn.lease-inclusion
    width: 100%
    order: -1
  .lease-distance-container
    width: 100%

@media (max-width: 599px)
  .view-shortlist-action-group
    top: 136px
    margin-bottom: 10px
  .filter-action-button
    align-self: flex-end

@media (max-width: 425px)
  .q-dialog__inner.q-dialog__inner--minimized
    padding: 10px!important
</style>
<style lang="sass">
@media (max-width:599px)
  .repayment_mobile
    .q-checkbox
      min-width: 50%;
    .q-checkbox__label,
    .q-checkbox__label button
      width: 100%
  .filter-action-button
    .q-expansion-item
      .q-item
        padding: 8px 10px
        .q-item__label
          font-weight: bold
      .q-item__section--avatar
        min-width: unset
        padding-right: 4px
  .view-shortlist-footer .calculator-button.q-btn .q-btn__wrapper
    padding: 0 12px
    font-size: 15px
</style>
